"use client";

import { ANIMATION_TIME } from "@faire/deprecated-retailer-visitor-shared/components/Header/HeaderNotifications/consts";
import { useIsOnNotificationsPage } from "@faire/deprecated-retailer-visitor-shared/components/Header/HeaderNotifications/hooks/useIsOnNotificationsPage";
import { useNotificationMessagePath } from "@faire/deprecated-retailer-visitor-shared/components/Header/HeaderNotifications/hooks/useNotificationMessagePath";
import { useNotificationPreview } from "@faire/deprecated-retailer-visitor-shared/components/Header/HeaderNotifications/hooks/useNotificationPreview";
import { useSetNotificationState } from "@faire/deprecated-retailer-visitor-shared/components/Header/HeaderNotifications/hooks/useSetNotificationState";
import { useHeaderPriorityStore } from "@faire/deprecated-retailer-visitor-shared/components/HeaderPriorityRenderer/HeaderPriorityPriovider";
import { useIsHeaderDropdownVisible } from "@faire/deprecated-retailer-visitor-shared/components/HeaderPriorityRenderer/useIsHeaderDropdownVisible";
import { isWebView } from "@faire/deprecated-retailer-visitor-shared/lib/user-agent/isWebView";
import { getRetailer } from "@faire/deprecated-retailer-visitor-shared/serialized-data/getRetailer";
import { Core } from "@faire/design-tokens";
import { setTimeoutIfPossible } from "@faire/next-utils/isomorphic/setTimeoutIfPossible";
import { Close } from "@faire/slate/icons/Close";
import { trackRetailerNotificationsP0NudgeClick } from "@faire/web-api--source/events/retailerNotifications/click/p0Nudge";
import { trackRetailerNotificationsP0NudgeDismiss } from "@faire/web-api--source/events/retailerNotifications/dismiss/p0Nudge";
import { trackRetailerNotificationsP0NudgeView } from "@faire/web-api--source/events/retailerNotifications/view/p0Nudge";
import { useStrictLocalization } from "@faire/web/common/localization";
import { useIsSSR } from "@faire/web/common/server-side-rendering/isSSR";
import { Button } from "@faire/web/slate-deprecated/Button";
import { Flex, Row } from "@faire/web/slate/Layout";
import { responsiveMediaQueries } from "@faire/web/slate/Theme/responsiveMediaQueries";
import { Span, Typography } from "@faire/web/slate/Typography";
import { useDoActionWhenVisible } from "@faire/web/ui/hooks/useDoActionWhenVisible";
import isUndefined from "lodash/isUndefined";
import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import styled, { css, keyframes } from "styled-components";

export const NotificationPreview = () => {
  const [{ isNotificationPreviewVisible }, setHeaderPriorityStore] =
    useHeaderPriorityStore(["isNotificationPreviewVisible"]);

  const { isAnyHeaderDropdownVisible } = useIsHeaderDropdownVisible();

  const { strictLocalize } = useStrictLocalization();
  const notificationPreview = useNotificationPreview();
  const isOnNotificationsPage = useIsOnNotificationsPage();
  const messagePath = useNotificationMessagePath(notificationPreview?.message);

  const { setNotificationHasBeenViewed, setNudgeHasBeenDismissed } =
    useSetNotificationState(notificationPreview?.token ?? "");
  const retailerToken = getRetailer()?.token;
  const isSSR = useIsSSR();
  const [animateOut, setAnimateOut] = useState(false);

  const ref = useDoActionWhenVisible(() => {
    trackRetailerNotificationsP0NudgeView(
      notificationPreview?.type ?? "",
      notificationPreview?.message?.text ?? "",
      notificationPreview?.client_identifier ?? "",
      notificationPreview?.token ?? ""
    );
  });

  const handleAnimateOutNudge = useCallback(() => {
    setAnimateOut(true);
    setTimeoutIfPossible(() => {
      setHeaderPriorityStore({
        isNotificationPreviewVisible: false,
      });
      setAnimateOut(false);
    }, ANIMATION_TIME);
  }, [setHeaderPriorityStore]);

  const handleDismiss = useCallback(() => {
    trackRetailerNotificationsP0NudgeDismiss(
      notificationPreview?.type ?? "",
      retailerToken ?? "",
      notificationPreview?.message?.text ?? "",
      notificationPreview?.client_identifier ?? "",
      notificationPreview?.token ?? ""
    );
    setNudgeHasBeenDismissed();
    handleAnimateOutNudge();
  }, [
    handleAnimateOutNudge,
    notificationPreview?.client_identifier,
    notificationPreview?.message?.text,
    notificationPreview?.token,
    notificationPreview?.type,
    retailerToken,
    setNudgeHasBeenDismissed,
  ]);

  const handleClick = () => {
    trackRetailerNotificationsP0NudgeClick(
      notificationPreview?.type ?? "",
      retailerToken ?? "",
      notificationPreview?.message?.text,
      notificationPreview?.client_identifier ?? "",
      notificationPreview?.token ?? ""
    );
    setNotificationHasBeenViewed();
    setNudgeHasBeenDismissed();
    handleAnimateOutNudge();
  };

  useEffect(() => {
    if (isAnyHeaderDropdownVisible && notificationPreview) {
      handleAnimateOutNudge();
    } else if (
      !isAnyHeaderDropdownVisible &&
      notificationPreview &&
      isUndefined(notificationPreview?.nudge_dismissed_at)
    ) {
      setHeaderPriorityStore({
        isNotificationPreviewVisible: true,
      });
    }
  }, [
    handleAnimateOutNudge,
    isAnyHeaderDropdownVisible,
    notificationPreview,
    setHeaderPriorityStore,
  ]);

  if (
    !isNotificationPreviewVisible ||
    isUndefined(notificationPreview) ||
    isOnNotificationsPage ||
    isSSR ||
    isWebView()
  ) {
    return null;
  }

  return (
    <AnimatedPreviewWrapper
      $animateOut={animateOut}
      justify="flex-end"
      align="flex-start"
      ref={ref}
    >
      <LinkWrapper
        to={messagePath}
        onClick={handleClick}
        data-test-id={`notification-preview-${notificationPreview.type}`}
      >
        <Row justify="space-between">
          <Typography variant="paragraphSansMedium">
            {notificationPreview.message?.prefix ? (
              <>
                <Span
                  variant="paragraphSansMedium"
                  color={`#${
                    notificationPreview.message.prefix.text_color_hex ??
                    "E65126"
                  }`}
                >
                  {notificationPreview.message.prefix.text}
                </Span>{" "}
              </>
            ) : null}
            {notificationPreview?.message?.text}
          </Typography>
        </Row>
      </LinkWrapper>
      <IconWrapper
        aria-label={strictLocalize({
          id: "diVxpc",
          defaultMessage: "close button",
          description: {
            text: "Close button for the banner",
          },
        })}
        onClick={handleDismiss}
        variant="tertiary"
        data-test-id="notification-preview-close-button"
      >
        <StyledClose fontSize="13px" strokeWidth={2} />
      </IconWrapper>
    </AnimatedPreviewWrapper>
  );
};

const slideInFromRight = keyframes`
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
`;

const slideOutToRight = keyframes`
  from {
    transform: translateX(0);
    opacity: 1;
  }
  to {
    transform: translateX(100%);
    opacity: 0;
  }
`;

const StyledClose = styled(Close)`
  &:hover {
    cursor: pointer;
  }
`;

const IconWrapper = styled(Button)`
  background: ${Core.surface.inverse};
  border-radius: 50%;
  height: 35px;
  width: 35px;
  padding: 10px;
  position: relative;
  top: 16px;
  right: 8px;

  &:hover {
    background: ${Core.surface.subdued};
  }
`;

const PreviewWrapper = styled(Flex)`
  position: absolute;
  right: 0px;
  width: 100%;
  box-shadow: -5px 10px 10px 0px rgba(0, 0, 0, 0.15);
  background: ${Core.surface.inverse};
  z-index: 300;
  ${responsiveMediaQueries.tabletAndAbove} {
    max-width: 392px;
  }
  &:hover:not(:has(${IconWrapper}:hover)) {
    background: ${Core.surface.subdued};
  }
`;

const LinkWrapper = styled(Link)`
  padding: 20px;
  display: flex;
  width: calc(100% - 35px);
`;

const AnimatedPreviewWrapper = styled(PreviewWrapper)<{ $animateOut: boolean }>`
  animation: ${(props) =>
    props.$animateOut
      ? css`
          ${slideOutToRight} 300ms forwards
        `
      : css`
          ${slideInFromRight} 300ms forwards
        `};
`;
